import { useEffect, useRef, useState } from "react";
import { Sparkles, X, Send } from "lucide-react";
import { API } from "@/lib/api";

// Large offset that always lands past the last message (smooth scroll-to-bottom).
const SCROLL_BOTTOM_OFFSET = 1_000_000_000;

export default function AIChat() {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    { id: `msg-${Date.now()}`, role: "assistant", content: "Hello — I'm the studio's assistant. Looking for a specific print, or want a recommendation?" },
  ]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [sessionId] = useState(() => `chat-${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: SCROLL_BOTTOM_OFFSET, behavior: "smooth" });
  }, [messages, open]);

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    setInput("");
    setMessages((m) => [
      ...m,
      { id: `u-${Date.now()}`, role: "user", content: text },
      { id: `a-${Date.now()}`, role: "assistant", content: "" },
    ]);
    setBusy(true);
    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text }),
      });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const lines = buf.split("\n");
        buf = lines.pop() || "";
        for (const line of lines) {
          if (!line.startsWith("data: ")) continue;
          const data = line.slice(6);
          if (data === "[DONE]") continue;
          setMessages((m) => {
            const copy = [...m];
            const last = copy[copy.length - 1];
            copy[copy.length - 1] = { ...last, content: last.content + data };
            return copy;
          });
        }
      }
    } catch (e) {
      setMessages((m) => [...m.slice(0, -1), { id: `a-err-${Date.now()}`, role: "assistant", content: "Sorry, something went wrong." }]);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        data-testid="ai-chat-toggle"
        onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 left-6 z-40 inline-flex items-center gap-2 px-4 py-3 rounded-full transition shadow-lg"
        style={{ background: "var(--ink)", color: "var(--paper)" }}
      >
        <span className="ai-dot" />
        <Sparkles className="w-4 h-4" />
        <span className="text-xs font-medium tracking-wider uppercase">Ask the Studio</span>
      </button>
      {open && (
        <div data-testid="ai-chat-panel" className="fixed bottom-24 left-6 z-40 w-[min(380px,calc(100vw-3rem))] h-[480px] flex flex-col rounded-xl overflow-hidden"
             style={{ background: "var(--paper)", border: "1px solid var(--rule)", boxShadow: "0 24px 60px rgba(26,27,31,0.25)" }}>
          <div className="flex items-center justify-between px-4 py-3 rule-b">
            <div className="flex items-center gap-2">
              <span className="ai-dot" />
              <span className="font-display font-medium">Studio Assistant</span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 hover:opacity-60"><X className="w-4 h-4" /></button>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3 text-sm">
            {messages.map((m) => (
              <div key={m.id} className={m.role === "user" ? "text-right" : ""}>
                <div className="inline-block px-3.5 py-2.5 max-w-[85%] text-left rounded-2xl"
                     style={m.role === "user"
                       ? { background: "var(--ink)", color: "var(--paper)" }
                       : { background: "var(--paper-2)" }}>
                  {m.content || (m.role === "assistant" && busy && <span className="opacity-60">…</span>)}
                </div>
              </div>
            ))}
          </div>
          <div className="rule-t p-3 flex gap-2" style={{ background: "var(--paper-2)" }}>
            <input
              data-testid="ai-chat-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && send()}
              placeholder="Ask about an edition…"
              className="flex-1 px-3 py-2 rounded-full text-sm outline-none"
              style={{ background: "var(--paper)", border: "1px solid var(--rule)" }}
            />
            <button data-testid="ai-chat-send" onClick={send} disabled={busy} className="btn-pill !px-4">
              <Send className="w-3.5 h-3.5" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
